import React, {useCallback} from 'react';

import ExcelJS from 'exceljs';
import {saveAs} from 'file-saver';
import {useUserEmail} from 'src/hooks/useUserEmail';
import {ExcelDownloadButton} from 'src/modules/downloads/components/excel-download-button';
import {getUserName} from 'src/modules/downloads/utils/get-user-name';
import {
    printChangeLog,
    printEventDetailInfo,
    printSheetHeader,
    printTableData,
    printTableDataHeader,
} from 'src/modules/event/download/print-data';
import {getUsersInfo} from 'src/modules/users-info/services';
import {ChangeRequest} from 'src/openapi/models/ChangeRequest';
import {EventDetailInfo} from 'src/openapi/models/EventDetailInfo';
import {User} from 'src/openapi/models/User';
import {useAppSelector} from 'src/store';

import {formatDate} from '@pb/kit-formatters';

type Props = {
    user: User | null,
    eventDetailInfo: EventDetailInfo,
    changeRequests: Array<ChangeRequest>,
};

export const EventExcelDownload = ({user, eventDetailInfo, changeRequests}: Props) => {
    const userEmail = useUserEmail();
    const userName = getUserName(userEmail, user);
    const usersInfo = useAppSelector(getUsersInfo);

    const onDownload = useCallback(async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Event', {views: [{showGridLines: false}]});

        printSheetHeader(worksheet, userName);
        printEventDetailInfo(worksheet, eventDetailInfo);
        printChangeLog(worksheet, changeRequests, usersInfo);
        printTableDataHeader(worksheet);
        printTableData(worksheet, eventDetailInfo.attributes);

        const buffer = await workbook.xlsx.writeBuffer();
        const date = formatDate(new Date(), 'YYYY_DD_MM_H_mm_ss');

        saveAs(new Blob([buffer]), `UD_Design_Tool_Event_${date}.xlsx`);
    }, [userName, eventDetailInfo, changeRequests, usersInfo]);

    return (
        <ExcelDownloadButton onDownload={onDownload}/>
    );
};
